import React from 'react';
import { useRouter } from 'next/router';
import * as Sentry from '@sentry/nextjs';
import { OperationResult } from 'urql';
import { Icon } from '@global-ecom/nitro-uds/elements';

import {
  CancelApplePayMutation,
  CancelApplePayMutationVariables,
  usePayWithApplePayMutation,
  useCancelApplePayMutation,
  useConfirmApplePayMutation,
  useUpdateBillingAddressMutation,
  useUpdateShippingAddressMutation,
  useUpdateShipmentMethodMutation,
} from '__generated__/graphql';
import { useTranslate } from 'hooks/useTranslations';
import { useRadial } from 'hooks/useRadial';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { isServer, PaymentMethod } from 'utils/constants';
import { useFeature } from 'hooks/useFeature';
import { event, AnalyticsEvents, paymentClickEvent } from 'utils/analytics';
import { useCart } from 'hooks/useCart';
import {
  canStartAppleSheet,
  ensureAddressFields,
  hasAllFieldsForTaxCalculation,
  transformShipmentMethod,
} from 'utils/payments/apple';
import { useApplePay } from 'hooks/useApplePay';
import { getForterToken } from 'utils/forter';
import { Button } from 'ui/elements/Button';

export const ApplePayButton = ({ disabled }: { disabled?: boolean }) => {
  const router = useRouter();
  const t = useTranslate();
  const { countryCode, currency, localizeUrlPath, env } = useSiteConfig();
  const [, applePay] = usePayWithApplePayMutation();
  const [, confirmApplePay] = useConfirmApplePayMutation();
  const [, cancelApplePay] = useCancelApplePayMutation();
  const { radialFingerPrint, radialFrontendContext } = useRadial();

  const [, updateShippingAddress] = useUpdateShippingAddressMutation();
  const [, updateBillingAddress] = useUpdateBillingAddressMutation();
  const [, updateShipmentMethod] = useUpdateShipmentMethodMutation();
  const [isAppleAvailable, setAppleAvailable] = React.useState(false);
  const isApplePayEnabled = useFeature(PaymentMethod.APPLE_PAY);
  const totalText = `Puma ${countryCode.toLocaleUpperCase()}`;
  const { applePaySupportedNetworks } = useSiteConfig();

  const { cart, cartResult } = useCart({
    pause: !isAppleAvailable || isServer,
  });

  const cancelApplePayCallback = async (
    variables: CancelApplePayMutationVariables
  ): Promise<OperationResult<CancelApplePayMutation>> => {
    return cancelApplePay(variables);
  };

  const {
    createLineItems,
    getShippingContactUpdateForMissingTaxFields,
    generateUpdateShippingAddressInputFromAppleEvent,
    getShippingContactUpdateForSuccess,
    getShippingContactUpdateForShippingAddressMutationError,
    mapAppleEventToAddressInput,
  } = useApplePay({ cartResult });

  React.useEffect(() => {
    if (canStartAppleSheet) {
      const canMakePayment = ApplePaySession.canMakePayments();

      const notUsingAdyen = !env.adyenClientKey;

      setAppleAvailable(canMakePayment && isApplePayEnabled && notUsingAdyen);
    }
  }, [isApplePayEnabled, env.adyenClientKey]);

  const handleApplePay = React.useCallback(async () => {
    try {
      if (!isApplePayEnabled || !cart) {
        return;
      }

      const cartHasProducts = cart.products.some(
        product => product.__typename !== 'GiftCardCartItem'
      );

      const session = new ApplePaySession(4, {
        countryCode: countryCode.toUpperCase(),
        currencyCode: currency.code,
        supportedNetworks: applePaySupportedNetworks,
        merchantCapabilities: ['supports3DS'],
        shippingMethods: cart.shipmentMethodInfo?.availableShipmentMethods
          .filter(m => (cartHasProducts ? true : m.id === 'Email'))
          .map(transformShipmentMethod),
        requiredShippingContactFields: [
          'postalAddress',
          'name',
          'phone',
          'email',
        ],
        requiredBillingContactFields: ['postalAddress', 'name'],
        lineItems: [
          {
            label: t('applePayItemsTotal'),
            amount: (cart.subTotal ?? 0).toFixed(2),
            type: 'final',
          },
          {
            label: t('applePayTax'),
            amount: (cart.taxTotal ?? 0).toFixed(2),
            type: 'final',
          },
          {
            label: t('applePayShipment'),
            amount: (cart.shipment?.shipmentMethod?.price ?? 0).toFixed(2),
            type: 'final',
          },
          {
            label: 'Order discount',
            amount: cart.orderAdjustments
              .reduce((sum, item) => sum + (item.price ?? 0), 0)
              .toFixed(2),
            type: 'final',
          },
          {
            label: t('appleShippingDiscount'),
            amount: cart.shippingAdjustments
              .reduce((sum, item) => sum + (item.price ?? 0), 0)
              .toFixed(2),
            type: 'final',
          },
        ],
        total: {
          label: totalText,
          amount: (cart.estimatedTotal ?? 0).toFixed(2),
        },
      });

      session.onvalidatemerchant = async event => {
        if (!cart?.id) {
          return session.abort();
        }

        const applePayOperationResult = await applePay({
          input: {
            basketId: cart.id,
            url: event.validationURL,
          },
        });

        if (!applePayOperationResult.data?.applePay) {
          return session.abort();
        }

        session.completeMerchantValidation(
          applePayOperationResult.data.applePay
        );
      };

      session.onshippingmethodselected = async event => {
        if (!cart) return;

        const updateShipmentOperationResult = await updateShipmentMethod({
          input: {
            shippingMethodId: event.shippingMethod.identifier,
            basketId: cart.id,
          },
        });

        if (!updateShipmentOperationResult.data?.updateShipmentMethod) {
          return session.completeShippingMethodSelection(createLineItems(cart));
        }

        session.completeShippingMethodSelection(
          createLineItems(
            updateShipmentOperationResult.data.updateShipmentMethod
          )
        );
      };

      session.oncancel = event => {
        if (!cart) return;

        cancelApplePayCallback({ basketId: cart.id });

        throw new Error(JSON.stringify(event));
      };

      /**
       * Event handler when the shipping information is changed.
       */
      session.onshippingcontactselected = async event => {
        if (!hasAllFieldsForTaxCalculation(event)) {
          return session.completeShippingContactSelection(
            getShippingContactUpdateForMissingTaxFields()
          );
        }

        const updateShippingAddressOperationResult =
          await updateShippingAddress({
            input: generateUpdateShippingAddressInputFromAppleEvent(event),
          });

        if (!updateShippingAddressOperationResult.data?.updateShippingAddress) {
          return session.completeShippingContactSelection(
            getShippingContactUpdateForShippingAddressMutationError()
          );
        }

        session.completeShippingContactSelection(
          getShippingContactUpdateForSuccess({
            addressResult:
              updateShippingAddressOperationResult.data.updateShippingAddress,
          })
        );
      };

      session.onpaymentauthorized = async event => {
        if (!cart) return;

        if (!ensureAddressFields(event.payment.shippingContact)) {
          await cancelApplePayCallback({ basketId: cart.id });

          return session.completePayment(
            ApplePaySession.STATUS_INVALID_SHIPPING_POSTAL_ADDRESS
          );
        }

        if (!ensureAddressFields(event.payment.billingContact)) {
          await cancelApplePayCallback({ basketId: cart.id });

          return session.completePayment(
            ApplePaySession.STATUS_INVALID_BILLING_POSTAL_ADDRESS
          );
        }

        const [shippingInput, billingInput] =
          mapAppleEventToAddressInput(event);

        const updateShippingAddressPromise = await updateShippingAddress({
          input: shippingInput,
        });

        if (updateShippingAddressPromise.error) {
          await cancelApplePayCallback({ basketId: cart.id });

          return session.completePayment(
            ApplePaySession.STATUS_INVALID_SHIPPING_POSTAL_ADDRESS
          );
        }

        const updateBillingAddressOperationResult = await updateBillingAddress({
          input: billingInput,
        });

        if (updateBillingAddressOperationResult.error) {
          await cancelApplePayCallback({ basketId: cart.id });

          return session.completePayment(
            ApplePaySession.STATUS_INVALID_BILLING_POSTAL_ADDRESS
          );
        }

        const pData = event.payment.token.paymentData;

        const confirmApplePayOperationResult = await confirmApplePay({
          input: {
            basketId: cart.id,
            paymentData: JSON.stringify(pData),
            radialFingerPrint,
            radialFrontendContext,
            forterToken: getForterToken(),
          },
        });

        if (!confirmApplePayOperationResult.data?.confirmApplePay) {
          return session.completePayment(ApplePaySession.STATUS_FAILURE);
        }

        const placedOrder =
          confirmApplePayOperationResult.data?.confirmApplePay;

        if (placedOrder) {
          window.sessionStorage.setItem('payment-method', 'Apple Pay');

          session.completePayment(ApplePaySession.STATUS_SUCCESS);

          router.push({
            pathname: localizeUrlPath('/checkout/confirmation'),
            query: {
              orderNo:
                confirmApplePayOperationResult.data.confirmApplePay.orderNo,
            },
          });
        }
      };

      session.begin();
    } catch (e) {
      Sentry.captureException(e);
    }
  }, [cart]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAppleAvailable) return null;

  return (
    <Button
      data-test-id="apple-pay-button"
      label="Apple Pay"
      className="w-full h-14 bg-white border-puma-black-700 hover:(bg-neutral-20 border-neutral-20)"
      disabled={disabled || !cart || cartResult.stale}
      onClick={() => {
        handleApplePay();
        event(AnalyticsEvents.PAYMENT_TYPE_SELECTED, {
          ecommerce: {
            name: AnalyticsEvents.PAYMENT_TYPE_SELECTED,
            value: 'Apple Pay',
          },
        });
        paymentClickEvent();
      }}
      type="button"
    >
      <Icon name="payment-apple-pay" size="9xl" />
    </Button>
  );
};
